import { Typography } from "antd";
import React, { useMemo } from "react";
import type { TextProps } from "antd/es/typography/Text";
import type { TitleProps } from "antd/es/typography/Title";
import classnames from "classnames";
import type { CurrencyIF } from "@/helpers";
import { formatThousands } from "@/helpers";
import { isRoundNegative, isRoundPositive, useNumberColorChange } from "@/helpers";
import { useCurrencyContext } from "@/context/CurrencyContext";

interface Props {
  value: number | string | undefined;
  prefix?: string | CurrencyIF | true;
  max2Digit?: boolean;
  isShowChange?: boolean;
  unbold?: boolean;
}

const NumberChange = (
  props: (Omit<TextProps, "prefix"> | Omit<TitleProps, "prefix">) &
    Props & {
      isText: boolean;
    },
) => {
  const { currentCurrency } = useCurrencyContext();
  const { isText, value, prefix, max2Digit, isShowChange, unbold } = props;

  const antdProps = useMemo<TextProps | TitleProps>(() => {
    const p = { ...props } as any;
    delete p.isText;
    delete p.value;
    delete p.prefix;
    delete p.max2Digit;
    delete p.useHideBalance;
    delete p.unbold;

    return p;
  }, [props]);

  const color = useNumberColorChange(typeof value === "number" ? value : 0);

  const stringNode = useMemo(
    () =>
      typeof value === "string"
        ? value
        : formatThousands(value, prefix === true ? currentCurrency : prefix, max2Digit, isShowChange),
    [isShowChange, max2Digit, prefix, value, currentCurrency],
  );

  const dom = useMemo<string>(() => stringNode, [stringNode]);
  if (isText) {
    return (
      <Typography.Text
        title={value?.toString()}
        {...antdProps}
        className={classnames(props.className, "whitespace-nowrap", { "font-normal": unbold })}
        style={{ ...color, ...antdProps.style }}
      >
        {dom}
      </Typography.Text>
    );
  } else if (isShowChange) {
    return (
      <Typography.Text
        type={
          typeof value === "number" && (max2Digit ? isRoundNegative(value) : value < 0)
            ? "danger"
            : typeof value === "number" && (max2Digit ? isRoundPositive(value) : value > 0)
            ? "success"
            : "secondary"
        }
        title={value?.toString()}
        {...antdProps}
        className={classnames(props.className, "whitespace-nowrap")}
        style={{ ...color, ...antdProps.style }}
      >
        {dom}
      </Typography.Text>
    );
  } else {
    return (
      <Typography.Title
        title={value?.toString()}
        {...antdProps}
        className={classnames(props.className, "whitespace-nowrap", { "font-normal": unbold })}
        style={{ ...color, ...antdProps.style }}
      >
        {dom}
      </Typography.Title>
    );
  }
};

export const TextNumberChange = (props: Omit<TextProps, "prefix"> & Props) => <NumberChange isText={true} {...props} />;

export const TitleNumberChange = (props: Omit<TitleProps, "prefix"> & Props) => (
  <NumberChange isText={false} {...props} />
);
